import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
} from "reactstrap";
import axios from "axios";
import { Award, CheckCircle, Shield, Zap, Check, Star } from "react-feather";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const MySwal = withReactContent(Swal);

const UpgradeAnnualModal = ({
  isOpen,
  toggle,
  currentPlanData,
  onUpgradeSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [annualPlans, setAnnualPlans] = useState({});

  const planInfo = currentPlanData?.data;
  const daysLeft = planInfo?.days_until_renewal ?? 0;
  const discountPercent = planInfo?.upgrade_discount_percent ?? 15;

  // Determine current tier: default upgrade choice to matching tier (or basic)
  const currentTier = planInfo?.plan_tier === "premium" ? "premium" : "basic";
  const [selectedTier, setSelectedTier] = useState("basic");

  useEffect(() => {
    if (isOpen) {
      setSelectedTier(currentTier);
      setErrorMsg("");
      setPricingLoading(true);
      const token = localStorage.getItem(process.env.REACT_APP_USERTOKEN);
      axios
        .get(`${process.env.REACT_APP_BASEURL}brand/invoice-plans`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const plans = response.data?.plans || [];
          setAnnualPlans(
            plans
              .filter((plan) => plan.key === "annual_basic" || plan.key === "annual_premium")
              .reduce((result, plan) => ({ ...result, [plan.key]: plan }), {})
          );
        })
        .catch(() => {
          setAnnualPlans({});
          setErrorMsg(
            "Current annual pricing is temporarily unavailable. Please try again in a moment."
          );
        })
        .finally(() => setPricingLoading(false));
    } else {
      // Prevent stale or unavailable pricing from flashing when the modal reopens.
      setPricingLoading(true);
    }
  }, [isOpen, currentTier]);

  // Pricing calculations
  const basicPlan = annualPlans.annual_basic;
  const premiumPlan = annualPlans.annual_premium;
  const basicConfigured = Boolean(basicPlan?.configured && basicPlan?.amount !== null);
  const premiumConfigured = Boolean(premiumPlan?.configured && premiumPlan?.amount !== null);
  const basicRegular = basicConfigured ? Number(basicPlan.amount) : null;
  const basicDiscounted = basicConfigured
    ? Number(basicPlan.discounted_amount ?? basicRegular * (1 - discountPercent / 100))
    : null;
  const basicSavings = basicConfigured ? basicRegular - basicDiscounted : null;
  const premiumRegular = premiumConfigured ? Number(premiumPlan.amount) : null;
  const premiumDiscounted = premiumConfigured
    ? Number(premiumPlan.discounted_amount ?? premiumRegular * (1 - discountPercent / 100))
    : null;
  const premiumSavings = premiumConfigured ? premiumRegular - premiumDiscounted : null;
  const selectedPlanConfigured = selectedTier === "premium" ? premiumConfigured : basicConfigured;

  const handleUpgrade = async () => {
    setErrorMsg("");
    if (!selectedPlanConfigured) {
      setErrorMsg("This annual plan's Stripe price is not configured. Please contact support.");
      return;
    }
    setLoading(true);

    const token = localStorage.getItem(process.env.REACT_APP_USERTOKEN);
    const baseUrl = process.env.REACT_APP_BASEURL;

    try {
      const requestToken = `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      const response = await axios.post(
        `${baseUrl}brand/upgrade-to-annual`,
        {
          target_tier: selectedTier,
          target_plan_key: `annual_${selectedTier}`,
          request_token: requestToken,
        },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.status) {
        toggle();
        const invoiceUrl = response.data?.hosted_invoice_url || response.data?.invoice_url;
        MySwal.fire({
          title: invoiceUrl ? "Upgrade Invoice Generated!" : "Upgrade Successful!",
          text: response.data.message || `Your plan has been upgraded to Annual ${selectedTier === "premium" ? "Premium" : "Basic"}.`,
          icon: "success",
          confirmButtonText: invoiceUrl ? "View & Pay Invoice" : "Great!",
          showCancelButton: !!invoiceUrl,
          cancelButtonText: "Close",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-secondary ml-1",
          },
          buttonsStyling: false,
        }).then((result) => {
          if (invoiceUrl && result.isConfirmed) {
            window.open(invoiceUrl, "_blank");
          }
        });
        if (onUpgradeSuccess) {
          onUpgradeSuccess();
        }
      } else {
        setErrorMsg(response.data?.message || "Failed to upgrade subscription.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "An unexpected error occurred while upgrading. Please try again.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">
        <div className="d-flex align-items-center">
          <Award className="text-warning mr-50" size={24} />
          <span className="font-weight-bolder">Exclusive Annual Upgrade Offer</span>
        </div>
      </ModalHeader>
      <ModalBody>
        {errorMsg && (
          <Alert color="danger" className="p-1 mb-2 font-small-3">
            {errorMsg}
          </Alert>
        )}

        <div className="text-center mb-2">
          <Badge color="light-primary" pill className="px-2 py-50 font-medium-1 mb-1">
            <Zap size={14} className="mr-50 text-primary" />
            Save {discountPercent}% on Every Annual Invoice
          </Badge>
          <p className="text-muted font-small-3 mb-0">
            Your quarterly plan renews in <strong>{daysLeft} days</strong>. Lock in an annual rate now with an exclusive{" "}
            <strong>{discountPercent}% annual discount</strong> applied to either Basic or Premium.
            All your remaining quarterly time is kept and <strong>one full calendar year</strong> is added.
          </p>
        </div>

        {/* ─── Selectable Annual Plans: Basic & Premium ─── */}
        {pricingLoading ? (
          <div className="text-center py-3 mb-2 rounded border bg-light">
            <Spinner color="primary" />
            <div className="font-weight-bold text-dark mt-1">
              Loading current secure pricing...
            </div>
            <div className="font-small-2 text-muted mt-25">
              We verify the latest Stripe prices and discount before you upgrade.
            </div>
          </div>
        ) : (
        <Row className="mb-2">
          {/* 1. Annual Basic Card */}
          <Col md="6" className="mb-1">
            <div
              onClick={() => basicConfigured && setSelectedTier("basic")}
              className={`p-1 rounded h-100 border transition-all ${
                selectedTier === "basic"
                  ? "border-primary bg-light-primary shadow-sm"
                  : "border-secondary-subtle bg-white"
              }`}
              style={{
                cursor: basicConfigured ? "pointer" : "not-allowed",
                opacity: basicConfigured ? 1 : 0.65,
                borderWidth: selectedTier === "basic" ? "2px" : "1px",
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <Badge color="light-success" pill className="font-small-1 mb-25">
                    {basicConfigured
                      ? `Save $${basicSavings.toLocaleString()} (${discountPercent}% Off)`
                      : "Pricing unavailable"}
                  </Badge>
                  <h4 className="font-weight-bolder text-dark mb-0">
                    Annually Basic
                  </h4>
                </div>
                <div className="text-right">
                  <div className="font-small-2 text-muted text-decoration-line-through">
                    {basicConfigured ? `$${basicRegular.toLocaleString()}/yr` : "—"}
                  </div>
                  <h3 className="font-weight-bolder text-primary mb-0">
                    {basicConfigured ? `$${basicDiscounted.toLocaleString()}` : "Unavailable"}
                  </h3>
                  <span className="font-small-1 text-muted">
                    {basicConfigured ? `/ year ($${(basicDiscounted / 12).toFixed(2)}/mo)` : ""}
                  </span>
                </div>
              </div>

              <p className="text-muted font-small-2 mb-75">
                Full 1-Year Access to Dietitian Directory & Direct Messaging with the configured upgrade savings.
              </p>

              <ul className="list-unstyled mb-1 font-small-2 text-secondary">
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>Full Dietitian Directory Access</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>Standard Campaign Seeding</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>Active Messaging Access</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>
                    Keep all <strong>{daysLeft} remaining days</strong> + one calendar year
                  </span>
                </li>
              </ul>

              <div className="text-right mt-75 pt-50 border-top">
                <span
                  className={`font-small-2 font-weight-bold ${
                    selectedTier === "basic" ? "text-primary" : "text-muted"
                  }`}
                >
                  {!basicConfigured
                    ? "Unavailable"
                    : selectedTier === "basic"
                      ? "✓ Selected for Upgrade"
                      : "Click to Select Basic"}
                </span>
              </div>
            </div>
          </Col>

          {/* 2. Annual Premium Card */}
          <Col md="6" className="mb-1">
            <div
              onClick={() => premiumConfigured && setSelectedTier("premium")}
              className={`p-1 rounded h-100 border transition-all ${
                selectedTier === "premium"
                  ? "border-warning bg-light-warning shadow-sm"
                  : "border-secondary-subtle bg-white"
              }`}
              style={{
                cursor: premiumConfigured ? "pointer" : "not-allowed",
                opacity: premiumConfigured ? 1 : 0.65,
                borderWidth: selectedTier === "premium" ? "2px" : "1px",
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <Badge color="light-warning" pill className="font-small-1 mb-25">
                    <Star size={10} className="mr-25 text-warning" />
                    {premiumConfigured
                      ? `VIP All-Access • Save $${premiumSavings.toLocaleString()}`
                      : "Pricing unavailable"}
                  </Badge>
                  <h4 className="font-weight-bolder text-dark mb-0">
                    Annually Premium
                  </h4>
                </div>
                <div className="text-right">
                  <div className="font-small-2 text-muted text-decoration-line-through">
                    {premiumConfigured ? `$${premiumRegular.toLocaleString()}/yr` : "—"}
                  </div>
                  <h3 className="font-weight-bolder text-warning mb-0">
                    {premiumConfigured ? `$${premiumDiscounted.toLocaleString()}` : "Unavailable"}
                  </h3>
                  <span className="font-small-1 text-muted">
                    {premiumConfigured ? `/ year ($${(premiumDiscounted / 12).toFixed(2)}/mo)` : ""}
                  </span>
                </div>
              </div>

              <p className="text-muted font-small-2 mb-75">
                Full 1-Year All-Inclusive VIP Partner Status with Featured Placement & Priority Support.
              </p>

              <ul className="list-unstyled mb-1 font-small-2 text-secondary">
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>VIP Partner Status & Directory Badging</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>Unlimited Campaign Seeding</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>Advanced Filtering & Priority Outreach</span>
                </li>
                <li className="d-flex align-items-center mb-50">
                  <Check size={14} className="text-success mr-50 flex-shrink-0" />
                  <span>
                    Keep all <strong>{daysLeft} remaining days</strong> + one calendar year
                  </span>
                </li>
              </ul>

              <div className="text-right mt-75 pt-50 border-top">
                <span
                  className={`font-small-2 font-weight-bold ${
                    selectedTier === "premium" ? "text-warning" : "text-muted"
                  }`}
                >
                  {!premiumConfigured
                    ? "Unavailable"
                    : selectedTier === "premium"
                      ? "✓ Selected for Upgrade"
                      : "Click to Select Premium"}
                </span>
              </div>
            </div>
          </Col>
        </Row>
        )}

        {/* ─── Upgrade Guarantee Info ─── */}
        <div className="bg-light rounded p-1 mb-2 border font-small-3 text-muted">
          <div className="d-flex align-items-center mb-50">
            <CheckCircle size={16} className="text-success mr-50 flex-shrink-0" />
            <span>
              <strong>Zero Time Lost:</strong> All {daysLeft} unused quarterly days are preserved and one full calendar year is added to your account.
            </span>
          </div>
          <div className="d-flex align-items-center mb-50">
            <CheckCircle size={16} className="text-success mr-50 flex-shrink-0" />
            <span>
              <strong>{discountPercent}% Annual Savings:</strong> The admin-configured discount is applied in Stripe now and remains on future invoices for this annual subscription.
            </span>
          </div>
          <div className="d-flex align-items-center">
            <Shield size={16} className="text-primary mr-50 flex-shrink-0" />
            <span>
              Secure transaction handled by Stripe. Your subscription cycle automatically transitions without any downtime.
            </span>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0 pt-0">
        <Button color="secondary" outline onClick={toggle} disabled={loading}>
          Maybe Later
        </Button>
        <Button
          color={selectedTier === "premium" ? "warning" : "primary"}
          onClick={handleUpgrade}
          disabled={loading || pricingLoading || !selectedPlanConfigured}
          className="font-weight-bold"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-50" />
              Processing Upgrade...
            </>
          ) : pricingLoading ? (
            <>
              <Spinner size="sm" className="mr-50" />
              Loading pricing...
            </>
          ) : (
            selectedPlanConfigured
              ? `Upgrade to Annual ${selectedTier === "premium" ? "Premium" : "Basic"} & Save ${discountPercent}% ($${(selectedTier === "premium" ? premiumDiscounted : basicDiscounted).toLocaleString()}/yr)`
              : "Annual pricing unavailable"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpgradeAnnualModal;
